import { cn } from '@Pages/pageRuntime/lib/utils';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as React from 'react';

// Tracks the popover's own trigger/anchor element so PopoverContent can portal
// into the Radix Dialog that contains it (if any), instead of the globally
// topmost open dialog. That keeps an unrelated dialog — e.g. a modal mid-close
// during an Inertia page swap — from capturing the popover and orphaning its
// content from <body> (the aria-hidden "not contained inside body" error).
const PopoverAnchorRefContext =
  React.createContext<React.MutableRefObject<HTMLElement | null> | null>(null);

const Popover = ({ children, ...props }: React.ComponentProps<typeof PopoverPrimitive.Root>) => {
  const anchorRef = React.useRef<HTMLElement | null>(null);

  return (
    <PopoverAnchorRefContext.Provider value={anchorRef}>
      <PopoverPrimitive.Root {...props}>{children}</PopoverPrimitive.Root>
    </PopoverAnchorRefContext.Provider>
  );
};

function useAnchorRef<T extends HTMLElement>(
  forwardedRef: React.Ref<T> | undefined
): React.RefCallback<T> {
  const anchorRef = React.use(PopoverAnchorRefContext);

  return React.useCallback(
    (node: T | null) => {
      if (anchorRef) {
        anchorRef.current = node;
      }
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [anchorRef, forwardedRef]
  );
}

const PopoverTrigger = ({
  ref,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) => {
  const composedRef = useAnchorRef(ref);
  return <PopoverPrimitive.Trigger ref={composedRef} {...props} />;
};
PopoverTrigger.displayName = PopoverPrimitive.Trigger.displayName;

const PopoverAnchor = ({ ref, ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) => {
  const composedRef = useAnchorRef(ref);
  return <PopoverPrimitive.Anchor ref={composedRef} {...props} />;
};
PopoverAnchor.displayName = PopoverPrimitive.Anchor.displayName;

const PopoverContent = ({
  className,
  align = 'center',
  sideOffset = 4,
  ref,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content>) => {
  'use no memo';

  // Resolve the container after commit: the trigger ref attaches during commit,
  // after this component first renders (when a popover starts open).
  const anchorRef = React.use(PopoverAnchorRefContext);
  const [container, setContainer] = React.useState<HTMLElement | null>(null);
  React.useLayoutEffect(() => {
    const dialogContent = anchorRef?.current?.closest<HTMLElement>('[data-radix-dialog-content]');
    if (!dialogContent) {
      setContainer(null);
      return;
    }
    // Skip portalling into dialog content that clips overflow — the CSS
    // transforms on the dialog create a containing block that breaks fixed
    // positioning, and overflow clipping hides popover dropdowns (e.g. the
    // phone-number country selector). Dialogs that explicitly opt out of
    // clipping (overflow-visible) still receive the portal.
    const { overflowY } = getComputedStyle(dialogContent);
    setContainer(overflowY === 'visible' ? dialogContent : null);
  }, [anchorRef]);

  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        // Shares the one overlay layer (z-50) with dialogs, sheets, selects and
        // menus, so whichever surface opened last is the one on top: a dialog
        // raised from a popover covers it, and a select opened inside a dialog
        // still sits above the dialog. Raising popovers above that layer put
        // them over modals that were opened from them.
        className={cn(
          'z-50 rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          className
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
};
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverAnchor, PopoverContent, PopoverTrigger };
